import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Star } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import SEO from "@/components/SEO";
import CaseStudies from "@/components/CaseStudies";
import { SECTIONS } from "@/config/siteSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [jobs, setJobs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/jobs`).then(res => setJobs(res.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/testimonials/approved`).then(res => setTestimonials(res.data.slice(0, 3))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const homeSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Magnus Kor Talent Partners",
      "url": "https://magnuskor.com",
      "logo": "https://magnuskor.com/logo512.png",
      "description": "Premium talent advisory firm for VC-backed SaaS startups. We build core teams across Engineering, Product, Growth, and Customer Success from IC to C-suite.",
      "parentOrganization": { "@type": "Organization", "name": "L&D Nexus Limited", "url": "https://ldnexus.com" },
      "address": { "@type": "PostalAddress", "streetAddress": "71-75 Shelton Street, Covent Garden", "addressLocality": "London", "postalCode": "WC2H 9JQ", "addressCountry": "GB" },
      "contactPoint": { "@type": "ContactPoint", "email": "recruitment@magnuskor.com", "contactType": "sales" },
      "sameAs": ["https://www.linkedin.com/company/magnus-kor"]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What does Magnus Kor Talent Partners do?", "acceptedAnswer": { "@type": "Answer", "text": "Magnus Kor is a talent advisory firm for VC-backed SaaS startups. We help founders build core teams across Engineering, Product, Growth, and Customer Success from individual contributors to C-suite executives." }},
        { "@type": "Question", "name": "What types of startups does Magnus Kor work with?", "acceptedAnswer": { "@type": "Answer", "text": "We work exclusively with VC-backed SaaS startups from Seed to Series C, typically scaling from 10 to 200+ employees." }},
        { "@type": "Question", "name": "Where is Magnus Kor located?", "acceptedAnswer": { "@type": "Answer", "text": "Our headquarters is in London, UK (71-75 Shelton Street, Covent Garden, WC2H 9JQ). We serve clients globally across the US, UK, Europe, and APAC." }},
        { "@type": "Question", "name": "How much does it cost to hire through Magnus Kor?", "acceptedAnswer": { "@type": "Answer", "text": "Our fees range from 15-25% of first-year CTC. Contingency hiring is 15-20% (pay only on success). Retained search is 20-25% with dedicated resources and a replacement guarantee." }},
        { "@type": "Question", "name": "Does Magnus Kor offer free hiring tools?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. We offer a free Global Salary Calculator, Hiring Cost Calculator, Equity Calculator, Hiring Readiness Checklist, and an AI-powered Smart Hire Planner at magnuskor.com/tools." }}
      ]
    }
  ];

  return (
    <div className="bg-[#FAF9F6]">
      <SEO 
        title="Talent Advisory for VC-Backed SaaS Startups"
        description="Magnus Kor Talent Partners is a premium talent advisory firm for VC-backed SaaS startups. We build core teams across Engineering, Product, Growth, and Customer Success from first hire to leadership bench."
        keywords="startup talent advisory, SaaS hiring partner, VC-backed startup recruiting, tech team building, engineering hiring, product manager jobs, growth marketing hiring, customer success hiring, Series A hiring, Series B talent strategy, startup HR consultancy, SaaS recruitment agency 2026"
        canonical="https://magnuskor.com/"
        jsonLd={homeSchema}
      />
      {/* Hero */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#F0EDE8] to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-8 md:px-16 pt-32 pb-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-10 animate-fade-up">
              <div>
                <p className="text-overline mb-6">Startup Talent Partner</p>
                <h1 className="heading-hero">
                  Teams that<br />
                  <span className="italic">scale</span> startups
                </h1>
              </div>
              
              <p className="text-body-lg max-w-md">
                We partner with VC-backed SaaS startups to build the core teams that drive growth—from your first product hire to your leadership bench.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/for-startups" className="btn-luxury btn-luxury-dark" data-testid="hero-cta-hire">
                  Build Your Team
                  <ArrowRight size={16} />
                </Link>
                <Link to="/for-talent" className="btn-luxury btn-luxury-outline" data-testid="hero-cta-talent">
                  Join Our Network
                </Link>
              </div>

              {/* Why founders choose us — client-benefit trust strip (honest, no client names) */}
              <div className="pt-2" data-testid="hero-credibility">
                <p className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-4">Why founders choose us</p>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {[
                    "Founder-led, senior delivery",
                    "90-day replacement guarantee",
                    "Transparent, published pricing",
                    "UK-registered firm",
                  ].map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-2 text-sm text-gray-600"
                      data-testid={`hero-credential-${item.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g,'')}`}
                    >
                      <Check size={14} className="text-[#B8860B] flex-shrink-0" strokeWidth={2.5} />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative animate-fade-up delay-200">
              <div className="aspect-[4/5] relative">
                <img loading="lazy"
                  src="/images/site/f35e3dafb24f-f35e3d.jpg"
                  alt="Magnus Kor Talent Partners — abstract brand visual in charcoal and gold"
                  className="w-full h-full object-cover"
                />
                {/* Focus Areas Card */}
                <div className="absolute -bottom-8 -left-8 bg-white p-6 shadow-2xl max-w-xs">
                  <p className="text-overline mb-3">What We Build</p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>Product & Engineering</p>
                    <p>Growth & Marketing</p>
                    <p>Customer Success</p>
                    <p>IC to Leadership</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 border-t border-r border-[#B8860B]/30" />
            </div>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-400">
          <span className="text-xs tracking-widest">SCROLL</span>
          <div className="w-px h-12 bg-gray-300" />
        </div>
      </section>

      {/* Two Paths */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <Link 
              to="/for-startups"
              className="group p-12 md:p-16 border-b md:border-b-0 md:border-r border-gray-100 hover:bg-[#FAF9F6] transition-colors duration-500"
              data-testid="home-for-startups"
            >
              <span className="text-overline">For Startups</span>
              <h2 className="heading-section mt-4 mb-6">Build your core team</h2>
              <p className="text-body mb-8 max-w-sm">
                From individual contributors to team leads—we help you hire the people who will define your next stage of growth.
              </p>
              <span className="inline-flex items-center gap-3 text-sm font-medium group-hover:text-[#B8860B] transition-colors">
                See How We Work
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </span>
            </Link>

            <Link 
              to="/for-talent"
              className="group p-12 md:p-16 hover:bg-[#FAF9F6] transition-colors duration-500"
              data-testid="home-for-talent"
            >
              <span className="text-overline">For Talent</span>
              <h2 className="heading-section mt-4 mb-6">Join a rocket ship</h2>
              <p className="text-body mb-8 max-w-sm">
                We connect exceptional people—at every level—with startups where they can have real impact and grow their careers.
              </p>
              <span className="inline-flex items-center gap-3 text-sm font-medium group-hover:text-[#B8860B] transition-colors">
                Explore Opportunities
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* What We Build - Simplified Teaser */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#B8860B] text-xs tracking-[0.25em] uppercase">What We Build</span>
              <h2 className="text-white heading-section mt-4 mb-6">
                The teams that drive growth
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                We focus on the core functions that determine whether a startup scales: Engineering, Product, Growth, and Customer Success. From your first IC to your leadership bench.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {["Engineering", "Product & Growth", "Go-to-Market", "Customer Success"].map((area, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-300">
                    <span className="w-1.5 h-1.5 bg-[#B8860B] rounded-full"></span>
                    {area}
                  </div>
                ))}
              </div>
              <Link to="/for-startups" className="btn-luxury btn-luxury-gold">
                See All Roles We Place
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="text-center">
              <div className="inline-block p-12 border border-gray-800">
                <p className="text-[#B8860B] text-xs tracking-[0.2em] uppercase mb-4">We Partner With</p>
                <p className="text-white font-display text-2xl mb-2">Seed → Series C</p>
                <p className="text-gray-500">VC-backed SaaS startups</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work - Teaser (hidden: duplicated on For Startups) */}
      {SECTIONS.homeRecruiterPlaybook && (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-overline">How We Work</span>
              <h2 className="heading-section mt-4 mb-6">Not the usual recruiter playbook</h2>
              <p className="text-body-lg mb-6">
                Most recruiters optimize for volume. We optimize for staying power. A hire that leaves at 6 months costs you more than an empty seat.
              </p>
              <p className="text-body mb-8">
                We take the time to understand your business before we source a single candidate. Small, curated shortlists. Support through onboarding. A 90-day guarantee.
              </p>

              <Link to="/for-startups" className="btn-luxury btn-luxury-outline">
                See Our Full Process
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="relative">
              <img loading="lazy"
                src="/images/site/unsplash-1714974528737-65c707.jpg"
                alt="Consultative hiring conversation with a startup team"
                className="w-full img-desaturate hover:filter-none transition-all duration-500"
              />
              <div className="absolute -bottom-6 -right-6 w-full h-full border border-[#B8860B]/30 -z-10"></div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Featured Roles */}
      {jobs.length > 0 && (
        <section className="py-32 bg-[#FAF9F6]">
          <div className="max-w-7xl mx-auto px-8 md:px-16">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-overline">Open Roles</span>
                <h2 className="heading-section mt-4">Current opportunities</h2>
              </div>
              <Link to="/jobs" className="hidden md:flex items-center gap-2 text-gray-500 hover:text-[#B8860B] text-sm transition-colors">
                View all roles
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="space-y-4">
              {jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="group block bg-white border border-gray-100 hover:border-[#B8860B]/30 p-8 transition-all duration-300"
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-display text-xl group-hover:text-[#B8860B] transition-colors">{job.title}</h3>
                        <span className="text-[10px] uppercase tracking-wider text-gray-400 border border-gray-200 px-2 py-0.5">
                          {job.seniority}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm">
                        {job.company} · {job.location} · {job.location_type}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-[#B8860B] text-xs tracking-wider uppercase">{job.department}</span>
                      <ArrowRight size={18} className="text-gray-300 group-hover:text-[#B8860B] group-hover:translate-x-2 transition-all" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Commitment */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="bg-[#FAF9F6] p-10 md:p-12">
              <span className="text-overline">Our Commitment</span>
              <h3 className="heading-card mt-4 mb-6">What we promise</h3>
              <div className="space-y-4">
                {[
                  "We only present candidates we'd hire ourselves",
                  "We'll tell you if your expectations don't match market reality",
                  "If a hire doesn't work out in 90 days, we re-run the search—on us",
                  "We're building a firm, not flipping placements"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-[#B8860B] mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="text-overline">Fresh Perspective</span>
              <h2 className="heading-section mt-4 mb-6">We're new. That's a feature.</h2>
              <p className="text-body mb-6">
                We don't have legacy processes or quotas to hit. We're building a recruitment firm the way we think it should work—focused on quality, honesty, and outcomes that last.
              </p>
              <p className="text-body">
                Every hire we make reflects on us. That's why we're selective about who we work with and obsessive about getting it right.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links - Internal Linking for SEO (hidden: nav + footer already cover this) */}
      {SECTIONS.homeExploreHub && (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl text-gray-900 mb-4">Explore Magnus Kor</h2>
            <p className="text-gray-500">Everything you need to hire, get hired, or plan your next growth stage.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/for-startups" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">For Startups</h3>
              <p className="text-gray-500 text-sm">Hiring models, pricing, and how we build your team.</p>
            </Link>
            <Link to="/tools" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">Free Tools</h3>
              <p className="text-gray-500 text-sm">Salary calculator, equity modeler, and AI hire planner.</p>
            </Link>
            <Link to="/insights" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">Insights</h3>
              <p className="text-gray-500 text-sm">Expert articles on startup hiring and compensation.</p>
            </Link>
            <Link to="/jobs" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">Open Roles</h3>
              <p className="text-gray-500 text-sm">Browse positions at VC-backed SaaS companies.</p>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Anonymised Placement Outcomes */}
      <CaseStudies variant="dark" />

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-[#FAF9F6]" data-testid="homepage-testimonials">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-display text-3xl text-[#1A1A1A] text-center mb-3">What Our Clients Say</h2>
            <p className="text-gray-500 text-center mb-12 max-w-xl mx-auto">Real feedback from founders, candidates, and partners.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map(t => (
                <div key={t.id} className="bg-white p-6 border border-gray-100 rounded" data-testid={`homepage-testimonial-${t.id}`}>
                  <div className="flex gap-0.5 mb-4">
                    {[1,2,3,4,5].map(n => <Star key={n} size={14} className={n <= t.rating ? "text-[#B8860B] fill-[#B8860B]" : "text-gray-200"} />)}
                  </div>
                  <p className="text-sm text-gray-600 mb-2 italic">"{t.outcome}"</p>
                  <p className="text-xs text-gray-400 mb-4">"{t.problem}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    {t.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${t.logo_url}`} alt={`${t.company_name} logo`} className="w-8 h-8 object-contain" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1A1A1A]">{t.contact_name}</p>
                      <p className="text-xs text-gray-400">{t.role}, {t.company_name}</p>
                      {t.talent_partner_name && (
                        <p className="text-[11px] text-[#B8860B] mt-1 flex items-center gap-1" data-testid={`homepage-credit-${t.id}`}>
                          <span className="opacity-60">with</span> {t.talent_partner_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center mt-8">
              <Link to="/testimonial/submit" className="text-sm text-[#B8860B] hover:underline">Share your experience with Magnus Kor</Link>
            </p>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-32 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
            Ready to build your team?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
            Whether you need your first engineer or your tenth PM, let's talk about what you're building.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/for-startups" className="btn-luxury btn-luxury-gold">
              Start a Conversation
              <ArrowRight size={16} />
            </Link>
            <Link to="/for-talent" className="btn-luxury border border-gray-600 text-white hover:border-[#B8860B] hover:text-[#B8860B]">
              I'm Looking for a Role
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
